/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
  UsageDialogConfig,
  usageDialogRoute,
} from '@gh/builder-shared';
import { BuilderPermissionSet, SpecTemplateStatus, UsageEntityType } from '@gh/builder-shared-data';
import {
  rendererIconByName,
  ROW_DELETED_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { AppRouter, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { controlTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogService, newFormControl } from '@gh/core-ui';
import {
  cursorTracker,
  GridDefinition,
  GridDefinitionFactory,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';

import { JobSpecTemplateListTabBusinessService } from '../../resources/job-spec-template-list-tab-business.service';
import { JobSpecTemplateListItem } from '../../resources/job-spec-template.model';

@Component({
  standalone: false,
  selector: 'gh-job-spec-template-list-tab',
  templateUrl: 'job-spec-template-list-tab.component.html',
})
export class JobSpecTemplateListTabComponent implements OnInit {
  dataSource: SlicedDataSource<JobSpecTemplateListItem>;
  showDeleted = newFormControl(false);
  cursor = newGridPaginationCursor();

  gridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private navigationService: BuilderNavigationService,
              private businessService: JobSpecTemplateListTabBusinessService,
              private commonActions: CommonActions,
              private pipelines: ListActionPipelines,
              private dialogService: DialogService,
              private userContext: UserContext,
              private gridDefinitionFactory: GridDefinitionFactory,
              private translations: TranslationService,
              private router: AppRouter) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();

    const tracker = this.routeData.tracker(controlTracker(this.showDeleted)).withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.load());

  }

  load(): void {
    this.dataSource = this.businessService.findAllAsDataSource({ showDeleted: this.showDeleted.value });
  }

  onOpen(template: JobSpecTemplateListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.JobAdmin.JobSpecTemplates.EDIT_VIEW)) {
      this.router.navigateToChild(['/setup/job-admin/job-spec-templates', template.id]);
    }
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/job-admin/job-spec-templates/new']);
  }

  onClone(template: JobSpecTemplateListItem): void {
    this.router.navigateToChild(
      ['/setup/job-admin/job-spec-templates/new'],
      { queryParams: { cloneFrom: template.id } });
  }

  onDelete(template: JobSpecTemplateListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_spec_templ_mark_as_deleted',
          params: { name: template.name },
        },
        confirmNotUsed: {
          id: 'msg_spec_templ_delete',
          params: { name: template.name },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.deactivateOrDelete(template, state.value)),
      },
      finish: () => this.load(),
    });

    flow.run({
      value: template,
    });
  }

  onUndelete(template: JobSpecTemplateListItem): void {
    const flow = this.pipelines.toggleItem({
      question: {
        id: 'msg_spec_templ_undelete',
        params: { name: template.name },
      },
      execute: this.commonActions.execute((state: CommonFlowState<JobSpecTemplateListItem>) =>
        this.businessService.activate(state.value)),
      finish: () => this.load(),
    });

    flow.run({
      value: template,
    });
  }

  onSearch(): void {
    this.load();
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId || void 0,
      entityType: entityType ? entityType : 'SPEC_TEMPLATE',
      label: label ? label : 'lbl_job_specs',
    });
  }

  onViewUsage(template: JobSpecTemplateListItem): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId: template.id,
      entityType: UsageEntityType.SPEC_TEMPLATE_USAGE_INFO,
    });
  }

  private buildGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual(
      [
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 400 },
        {
          field: 'isForRfq',
          headerName: this.translations.get('lbl_default_for_site_inspection'),
          width: 200,
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
      ],
      {
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({ data }: { data: JobSpecTemplateListItem }) =>
            data.statusValue === SpecTemplateStatus.Deleted,
        },
        getRowId: (params) => String(params.data.id)
      });
  }
}
